import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const inputVariants = cva(
  "flex w-full min-w-0 rounded-md border px-3 transition-[color,box-shadow,border-color] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:border-0 file:bg-transparent file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      inputSize: {
        default: "h-9 py-2 text-[14px] leading-[20px]",
        sm: "h-9 py-2 text-[14px] leading-[20px]",
      },
    },
    defaultVariants: {
      inputSize: "default",
    },
  },
);

interface InputProps extends Omit<React.ComponentProps<"input">, "size">, VariantProps<typeof inputVariants> {
  description?: React.ReactNode;
}

function Input({ className, type, inputSize, description, ...props }: InputProps) {
  return (
    <>
      <input
        type={type}
        data-slot="input"
        className={cn(
          inputVariants({ inputSize }),
          // Base styles
          "border-input bg-background text-foreground",
          // Dark mode base
          "dark:border-input dark:bg-background dark:text-foreground",
          // Focus state
          "focus-visible:border-[#8c72e2] focus-visible:ring-[3px] focus-visible:ring-[rgba(133,86,233,0.2)]",
          "dark:focus-visible:border-[#8c72e2] dark:focus-visible:bg-[#161027]",
          // Error state
          "aria-invalid:border-red-600 aria-invalid:ring-[3px] aria-invalid:ring-[rgba(220,38,38,0.2)]",
          "dark:aria-invalid:border-red-600",
          className,
        )}
        {...props}
      />
      {description && <div className="mt-2 text-sm text-muted-foreground">{description}</div>}
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Input, inputVariants };
